import React from 'react';
import PropTypes from 'prop-types';
import useFlags from '@/hooks/useFlags';
import { GradeByType } from '@/components/staffer/NotebookGradeByMenu/types';
import { NumberField } from 'common/Forms';
import { SelectField, optionList, Header } from '../Util';
import styles from './GradesForm.module.scss';
import Text from '../../../../ui/Text';
import Tooltip, { TCITooltip } from '../../../../ui/Tooltip';

const validateNotesGrade = (value) => {
  let error = false;

  if (isNaN(value)) {
    error = true;
  }
  else if (value > 100 || value < 1) {
    error = true;
  }
  else if (value % 1 !== 0) {
    error = true;
  }

  return error ? 'Default points value is invalid. Must be an integer value from 1 to 100' : null;
};

const GradesForm = ({ form }) => {
  const { gradeByAssignment } = useFlags();

  const gradeByOptions = [
    { label: 'Section', value: GradeByType.Section },
    { label: 'Lesson', value: GradeByType.Lesson },
  ];
  if (gradeByAssignment) gradeByOptions.push({ label: 'Assignment', value: GradeByType.Assignment });

  const headerText = gradeByAssignment ?
    'Default Grading for Reading Assignments' :
    'Select your default grading type and points per Notebook';

  const gradeByFieldLabel = gradeByAssignment ?
    'Reading Assignment Grading Preference' :
    'Grade notebooks by';

  const gradeByFieldTooltip = gradeByAssignment ?
    'Update this setting to apply grading by lesson, section, or assignment for all notebooks.' :
    `Changing this setting will update all notebooks to grade by lesson or section,
      except for lessons for which you've already graded a notebook.`;

  const lessonTooltip = `Changing this setting will update the max possible score of all
    notebooks, except for notebook lesson scores that you have already set on the Grades page.`;
  const sectionTooltip = `Changing this setting will update the max possible score of all
    notebooks, except for notebook section scores that you have already set on the Grades page.`;

  return (
    <>
      <Header text={headerText} />

      <SelectField
        label={gradeByFieldLabel}
        labelClass="tw-w-1/2"
        name="grade_notebooks_by"
        options={gradeByOptions}
        sectionHeader="tw-inline"
        tooltip={gradeByFieldTooltip}
        onChange={form.mutators.handleSelect}
      />

      <span className={styles.numberFieldContainer}>
        <div className="tw-flex tw-items-center tw-w-1/2">
          <Text asChild variant="label" size="sm">
            <label htmlFor="notebook_possible_points_field">Default Score for &quot;Grade by Lesson&quot;</label>
          </Text>
          <TCITooltip>
            <Tooltip.Trigger asChild><i className="fa fa-question-circle tw-ml-1" /></Tooltip.Trigger>
            <Tooltip.Content>
              {lessonTooltip}
            </Tooltip.Content>
          </TCITooltip>
        </div>
        <NumberField
          hideLabel
          useTextComponent
          endText={<Text size="sm">per lesson</Text>}
          name="notebook_possible_points"
          tooltipText={lessonTooltip}
          validate={validateNotesGrade}
        />
        per lesson
      </span>

      <span className={styles.numberFieldContainer}>
        <div className="tw-flex tw-items-center tw-w-1/2">
          <Text asChild variant="label" size="sm">
            <label htmlFor="section_possible_points_field">Default Score for &quot;Grade by Section&quot;</label>
          </Text>
          <TCITooltip>
            <Tooltip.Trigger asChild><i className="fa fa-question-circle tw-ml-1" /></Tooltip.Trigger>
            <Tooltip.Content>
              {sectionTooltip}
            </Tooltip.Content>
          </TCITooltip>
        </div>
        <div className="tw-flex tw-items-center">
          <NumberField
            hideLabel
            useTextComponent
            name="section_possible_points"
            tooltipText={sectionTooltip}
            validate={validateNotesGrade}
          />
          per section
        </div>
      </span>

      <div className="tw-mt-5">
        <Header text="Select what results you want highlighted in Grades Page" />
      </div>

      <SelectField
        label="Class Trend Threshold"
        name="trends_highlight"
        options={optionList(0, 100, 5, '%')}
        tooltip="Highlights questions with a class average lower than the percentage you set."
        onChange={form.mutators.handleSelect}
      />

      <SelectField
        label="Low Score Threshold"
        name="student_challenge_highlight"
        options={[{ label: 'None', value: -1 }, ...optionList(0, 100, 5, '%')]}
        tooltip="Highlights individual scores lower than the percentage you set."
        onChange={form.mutators.handleSelect}
      />
    </>
  );
};

GradesForm.propTypes = {
  form: PropTypes.shape({
    mutators: PropTypes.shape({
      handleSelect: PropTypes.func
    })
  }).isRequired,
};

export default GradesForm;
